/**
 * 安聆 SafeListen - 基础模式切换模块
 * 监测端/接收端一键切换，UI 联动：监测端显示配对码，接收端显示输入框
 */
import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Form, message } from 'antd';
import { validatePairingCodeFormat, normalizePairingInput, getPairingCodeDisplay } from '../utils/hashUtils';
import { supabase, TABLE_ACTIVE_CODES } from '../utils/supabase';
import './ModeSwitch.css';

const PAIRING_INPUT_MAXLENGTH = 12;

export default function ModeSwitch({
  isMonitor,
  onModeChange,
  pairingCode,
  boundPairingCode,
  onPairingSubmit,
  onReceiverUnbind,
  receiverBound,
}) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [inputValue, setInputValue] = useState('');

  const handleSwitch = useCallback(() => {
    onModeChange?.(!isMonitor);
    form.resetFields();
    setInputValue('');
  }, [isMonitor, onModeChange, form]);

  const handleInputChange = (e) => {
    const v = e.target.value;
    setInputValue(v);
    form.setFieldsValue({ pairing: v });
  };

  const handleReceiverSubmit = async (values) => {
    const raw = (values.pairing || inputValue || '').trim().replace(/\s/g, '');
    const normalized = normalizePairingInput(raw);
    if (!validatePairingCodeFormat(normalized)) {
      message.warning(
        isMonitor
          ? '请输入正确格式：动物名-6位码（如：小熊-3GQ2Y8）'
          : 'Please enter format: Animal-6Code (e.g. Bear-3GQ2Y8)'
      );
      return;
    }
    const hashPart = normalized.split('-')[1]?.trim().toUpperCase();
    if (!hashPart || hashPart.length !== 6) {
      message.warning(t('guard.codeInvalid'));
      return;
    }
    if (!supabase) {
      message.error(t('guard.codeInvalid'));
      return;
    }
    // 清理已过期的无人配对码，节省资源
    await supabase.from(TABLE_ACTIVE_CODES).delete().lt('expires_at', new Date().toISOString());
    const { data, error } = await supabase.from(TABLE_ACTIVE_CODES).select('hash').eq('hash', hashPart).single();
    if (error || !data) {
      message.error(t('guard.codeInvalid'));
      return;
    }
    // 有人连接后取消过期，避免已连接码被清理
    await supabase.from(TABLE_ACTIVE_CODES).update({ expires_at: null }).eq('hash', hashPart);
    onPairingSubmit?.(normalized);
    message.success(t('guard.connectSuccess'));
  };

  return (
    <div className="safelisten-card mode-switch">
      <div className="mode-switch-row">
        <Button
          type="default"
          className="safelisten-btn-secondary"
          onClick={handleSwitch}
        >
          {isMonitor ? t('mode.switchToReceiver') : t('mode.switchToMonitor')}
        </Button>
      </div>

      {isMonitor && (
        <div className="pairing-display monitor-only">
          <span className="pairing-label">{t('mode.pairingCode')}：</span>
          <span
            className="safelisten-highlight pairing-code pairing-code-copy"
            title={t('mode.copyCode')}
            onClick={() => {
              if (!pairingCode) return;
              navigator.clipboard.writeText(getPairingCodeDisplay(pairingCode, t)).then(() => message.success(t('mode.copied'))).catch(() => {});
            }}
          >
            {pairingCode ? getPairingCodeDisplay(pairingCode, t) : '—'}
          </span>
        </div>
      )}

      {!isMonitor && (
        <Form form={form} onFinish={handleReceiverSubmit} layout="vertical">
          <Form.Item name="pairing" noStyle>
            <Input
              value={receiverBound ? (t('mode.connected') + getPairingCodeDisplay(boundPairingCode || '', t)) : inputValue}
              onChange={handleInputChange}
              placeholder={t('mode.pairingPlaceholder')}
              maxLength={PAIRING_INPUT_MAXLENGTH}
              size="large"
              className="pairing-input"
              disabled={!!receiverBound}
              title={receiverBound ? t('mode.copyCode') : undefined}
              onClick={receiverBound && boundPairingCode ? () => {
                navigator.clipboard.writeText(getPairingCodeDisplay(boundPairingCode, t)).then(() => message.success(t('mode.copied'))).catch(() => {});
              } : undefined}
            />
          </Form.Item>
          {receiverBound ? (
            <Button
              type="default"
              className="safelisten-btn-secondary"
              style={{ marginTop: 8 }}
              onClick={() => {
                onReceiverUnbind?.();
                setInputValue('');
                form.setFieldsValue({ pairing: '' });
                message.success(t('guard.unbindSuccess'));
              }}
            >
              {t('guard.unbind')}
            </Button>
          ) : (
            <Button
              type="default"
              htmlType="submit"
              className="safelisten-btn-secondary"
              style={{ marginTop: 8 }}
            >
              {t('mode.connect')}
            </Button>
          )}
        </Form>
      )}
    </div>
  );
}
